import { inject, type InjectionKey } from 'vue';

/**
 * Picks one ERP record in the ERP's own grid — the same `EntityPicker` the
 * ERP's create dialogs use („Angebot neu“): its columns, column filters, sort
 * and pages (I-3178). The ERP implements it in its shell slice
 * (`@erp/connect/shell`), the Connect shell provides it to every app, so an
 * app without ERP code of its own (Calc) picks a customer or a quote the way
 * the ERP does.
 */
export interface ErpRecordPickRequest {
  /** Catalog resource, camelCase plural as the ERP's picker presets name it: `customers`, `salesQuotes`, … */
  readonly resource: string;
  /** Dialog title. */
  readonly title: string;
  /** Fixed pre-filters, AND-combined into every read: column → allowed values (`status: ['open']`). */
  readonly baseFilter?: Readonly<Record<string, readonly (string | number)[]>>;
  /** Sort while the user has stored none for the grid (`erp.grid.<table>` wins). */
  readonly sort?: { readonly column: string; readonly order: 'asc' | 'desc' };
}

/** The picked record: its id and the label the ERP names it by (matchcode, number, name). */
export interface ErpRecordPick {
  readonly id: number;
  readonly label: string;
}

export interface ErpRecordPicker {
  /**
   * Whether the ERP can be reached right now (the module has a connected
   * client, per the shell's `/v1/modules`). The service is provided whether or
   * not the ERP runs, so its presence says nothing — ask this (I-3178 review).
   */
  available(): boolean;
  /** Opens the dialog; resolves with the picked record, or null when it was closed without one. */
  pick(request: ErpRecordPickRequest): Promise<ErpRecordPick | null>;
}

/** Provided by the Connect shell. Absent in standalone module builds. */
export const ErpRecordPickerKey: InjectionKey<ErpRecordPicker> = Symbol('ErpRecordPicker');

/** The ERP record picker when running inside the Connect shell; otherwise `undefined`. */
export function useErpRecordPicker(): ErpRecordPicker | undefined {
  return inject(ErpRecordPickerKey, undefined);
}
